import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import SiteFooter from '../components/SiteFooter';
import { toast } from 'sonner';
import {
  ArrowLeft, Upload, FileText, Trash2, Mic, MicOff,
  CheckCircle2, Circle, RefreshCw, ChevronRight, Volume2, Sparkles,
} from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import LessonSummaryMenu from '../components/LessonSummaryMenu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * LearnTopic — the entire Learn Mode lifecycle for a single topic, rendered
 * as a phase-driven wizard: materials → assessment → plan → tutoring → notes.
 *
 * Voice tutoring reuses the same pause-tolerant SpeechRecognition + ElevenLabs
 * audio pattern that powers /live-coaching, without importing that page (kept
 * inline to avoid cross-page coupling and prop drilling).
 */
const LearnTopic = () => {
  const { topicId } = useParams();
  const navigate = useNavigate();

  // ---- top-level topic state ----
  const [topic, setTopic] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);   // any global mutation in flight

  // ---- phase-specific state ----
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [notes, setNotes] = useState([]);

  // ---- tutoring session state ----
  const [transcript, setTranscript] = useState([]); // {role:'user'|'sage', text}
  const [textInput, setTextInput] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [interimText, setInterimText] = useState('');

  // ---- audio + speech refs ----
  const recognitionRef = useRef(null);
  const isRecordingRef = useRef(false);
  const finalTranscriptRef = useRef('');
  const audioElementRef = useRef(null);
  // Kept in a ref so the window-level pointerup safety net (registered once
  // per recording session) can call the latest stopRecording without becoming
  // stale on re-renders.
  const stopRecordingRef = useRef(() => {});
  // Base64 of the last SAGE audio — lets us re-play on demand if browser
  // autoplay policy or a transient error blocked the first play() call.
  const [lastAudioB64, setLastAudioB64] = useState(null);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('sage_token');
    if (!token) {
      navigate('/login');
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const fetchTopic = useCallback(async () => {
    const headers = authHeaders();
    if (!headers) return;
    try {
      const res = await axios.get(`${API}/learn/topics/${topicId}`, { headers });
      setTopic(res.data.topic);
      setMaterials(res.data.materials || []);
      // Seed local phase state from server.
      setQuestions(res.data.topic?.assessment_questions || []);
      setAnswers(res.data.topic?.assessment_answers?.length
        ? res.data.topic.assessment_answers
        : new Array(res.data.topic?.assessment_questions?.length || 0).fill(''));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Failed to load topic');
      navigate('/learn');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, topicId, navigate]);

  const fetchNotes = useCallback(async () => {
    const headers = authHeaders();
    if (!headers) return;
    try {
      const res = await axios.get(`${API}/learn/topics/${topicId}/notes`, { headers });
      setNotes(res.data || []);
    } catch (err) {
      // Non-fatal — notes are supplementary. Surface in dev only.
      if (process.env.NODE_ENV !== 'production') console.debug('fetchNotes failed:', err);
    }
  }, [authHeaders, topicId]);

  useEffect(() => {
    fetchTopic();
    fetchNotes();
  }, [fetchTopic, fetchNotes]);

  // ==================== Speech recognition setup ====================
  useEffect(() => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';

    rec.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) {
          finalTranscriptRef.current =
            (finalTranscriptRef.current + ' ' + r[0].transcript).replace(/\s+/g, ' ').trim();
        } else {
          interim += r[0].transcript;
        }
      }
      setInterimText((finalTranscriptRef.current + ' ' + interim).replace(/\s+/g, ' ').trim());
    };

    rec.onend = () => {
      // Auto-restart on browser silence-stop so long pauses don't kill the mic.
      if (isRecordingRef.current) {
        try { rec.start(); } catch (e) {
          if (process.env.NODE_ENV !== 'production') {
            console.debug('SR restart skipped:', e?.message);
          }
        }
      }
    };

    rec.onerror = (event) => {
      if (event.error === 'not-allowed') {
        toast.error('Microphone permission denied.');
      } else if (event.error !== 'no-speech' && event.error !== 'aborted') {
        console.error('Speech recognition error:', event.error);
      }
    };

    recognitionRef.current = rec;
    return () => {
      isRecordingRef.current = false;
      try { rec.stop(); } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.debug('rec.stop cleanup:', err);
      }
    };
    // One-time speech-recognition init on mount. Including the callback
    // closures (onresult, onerror, state setters) in deps would re-mount
    // the recognizer on every render — breaking live transcription.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==================== Phase 1: Materials ====================
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const headers = authHeaders();
    if (!headers) return;
    const form = new FormData();
    form.append('file', file);
    setBusy(true);
    try {
      await axios.post(`${API}/learn/topics/${topicId}/materials`, form, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded ${file.name}`);
      await fetchTopic();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to upload material');
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    if (!window.confirm('Remove this material?')) return;
    try {
      await axios.delete(`${API}/learn/materials/${materialId}`, { headers: authHeaders() });
      toast.success('Material removed');
      fetchTopic();
    } catch {
      toast.error('Failed to remove material');
    }
  };

  const handleGenerateAssessment = async () => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/learn/topics/${topicId}/assess`,
        {},
        { headers: authHeaders() }
      );
      setQuestions(res.data.questions || []);
      setAnswers(new Array((res.data.questions || []).length).fill(''));
      toast.success('Diagnostic ready — answer honestly');
      await fetchTopic();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Assessment generation failed');
    } finally {
      setBusy(false);
    }
  };

  // ==================== Phase 2: Assessment ====================
  const handleSubmitAnswers = async () => {
    if (answers.some((a) => !a.trim())) {
      toast.error('Answer every question — even a short one is fine');
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/learn/topics/${topicId}/answer`,
        { answers },
        { headers: authHeaders() }
      );
      toast.success('Answers saved — generating your plan');
      await handleGeneratePlan();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save answers');
      setBusy(false);
    }
  };

  // ==================== Phase 3: Plan ====================
  const handleGeneratePlan = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/learn/topics/${topicId}/plan`, {}, { headers: authHeaders() });
      toast.success('Learning plan ready');
      await fetchTopic();
      // auto-kick off the first tutor turn so learners see immediate value
      await sendTutorTurn('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Plan generation failed');
    } finally {
      setBusy(false);
    }
  };

  // ==================== Phase 4: Tutoring ====================
  /**
   * Jump the tutor loop back to any previously-visited stage so the
   * learner can revisit material. Called from the left-rail stage card
   * clicks AND from the "Repeat lesson" toast action that fires on
   * stage completion. Does NOT un-complete the stage server-side — we
   * preserve the completion % so the Plan rail stays truthful.
   */
  const repeatStage = useCallback(async (stageIdx) => {
    const headers = authHeaders();
    if (!headers) return;
    try {
      const res = await axios.post(
        `${API}/learn/topics/${topicId}/stages/${stageIdx}/repeat`,
        {},
        { headers },
      );
      toast.success(`Repeating: ${res.data.stage_title}`);
      // Reset the transcript so the right pane shows a fresh re-opening
      // of the lesson rather than mixing it with the previous stage's
      // chatter. Audio playback element is left intact.
      setTranscript([]);
      await fetchTopic();
      // Auto-kick a fresh opening turn for the repeated stage so the
      // learner immediately hears SAGE re-teach the concept.
      await sendTutorTurn('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not repeat that lesson');
    }
    // sendTutorTurn is defined below; it closes over refs/state we don't
    // want to add as deps (fetchTopic/sendTutorTurn would loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeaders, topicId, fetchTopic]);

  const playAudio = useCallback((b64) => {
    if (!b64) return;
    setLastAudioB64(b64);
    try {
      if (audioElementRef.current) {
        audioElementRef.current.pause();
        audioElementRef.current = null;
      }
      const audio = new Audio(`data:audio/mpeg;base64,${b64}`);
      audio.preload = 'auto';
      audioElementRef.current = audio;
      setIsSpeaking(true);
      audio.onended = () => setIsSpeaking(false);
      audio.onerror = (e) => {
        console.warn('Tutor audio element error:', e);
        setIsSpeaking(false);
      };
      const p = audio.play();
      if (p && typeof p.then === 'function') {
        p.catch((err) => {
          // Browser blocked autoplay (or another transient failure). Surface
          // it — the "Play SAGE's voice" button below the last message lets
          // the user manually unlock audio with a single click.
          console.warn('Tutor audio play() rejected:', err?.name, err?.message);
          setIsSpeaking(false);
          if (err?.name === 'NotAllowedError') {
            toast.info('Tap the speaker icon on SAGE’s message to hear it', {
              duration: 4000,
            });
          }
        });
      }
    } catch (err) {
      console.warn('Tutor audio setup failed:', err);
      setIsSpeaking(false);
    }
  }, []);

  const replayLastAudio = () => {
    if (lastAudioB64) playAudio(lastAudioB64);
  };

  // Holds the last user input that failed to reach SAGE — surfaces a Retry
  // button so the learner never has to re-speak their question after a
  // timeout or transient failure.
  const [failedInput, setFailedInput] = useState(null);

  const sendTutorTurn = async (input) => {
    setBusy(true);
    setFailedInput(null); // clear any prior failure the moment we start
    const trimmed = (input || '').trim();
    // Optimistic push for the user's message so they see it right away.
    if (trimmed) {
      setTranscript((t) => [...t, { role: 'user', text: trimmed }]);
    }
    // Slow-turn warning: on a long conversation with a big material payload
    // Gemini + TTS can take 30–90 s. Reassure the user that we're still
    // working after ~15 s so they don't refresh mid-request.
    const slowTimer = setTimeout(() => {
      toast.info('Still thinking — long answers on rich material can take a moment', {
        duration: 4000,
      });
    }, 15000);
    try {
      const res = await axios.post(
        `${API}/learn/topics/${topicId}/tutor`,
        { user_input: input || '' },
        { headers: authHeaders(), timeout: 180000 } // 180 s — long-session upper bound
      );
      setTranscript((t) => [...t, { role: 'sage', text: res.data.text }]);
      playAudio(res.data.audio);
      // Refresh topic to catch stage advances / plan completion.
      if (res.data.stage_complete || res.data.plan_complete) {
        // Capture the index of the stage that was *just* completed so a
        // "Repeat lesson" toast action can jump back to it. The server
        // response doesn't carry it, but we know it was the index we
        // were sitting on before this turn fired.
        const justCompletedIdx = currentStageIdx;
        toast.success(
          res.data.plan_complete ? 'Plan complete!' : 'Stage complete',
          {
            action: {
              label: 'Repeat lesson',
              onClick: () => repeatStage(justCompletedIdx),
            },
            duration: 8000,
          },
        );
        try {
          await axios.post(
            `${API}/learn/topics/${topicId}/notes/generate`,
            {},
            { headers: authHeaders() }
          );
          fetchNotes();
        } catch (e) {
          console.debug('Auto note gen failed:', e?.message);
        }
        fetchTopic();
      }
    } catch (err) {
      // Categorize the failure so the message is useful.
      const isTimeout = err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '');
      const status = err?.response?.status;
      const serverDetail = err?.response?.data?.detail;
      let msg;
      if (isTimeout) {
        msg = 'SAGE took over 3 minutes to respond. Your message is saved — tap Retry.';
      } else if (status && status >= 500) {
        msg = serverDetail || `SAGE error (${status}). Tap Retry.`;
      } else if (!err?.response) {
        // No response → network drop, backend restart, etc.
        const codeSuffix = err?.code ? ` [${err.code}]` : '';
        msg = `Connection interrupted${codeSuffix}. Your message is saved — tap Retry.`;
      } else {
        msg = serverDetail || `Tutor turn failed (${status}). Tap Retry.`;
      }
      // Log full detail for debuggability without leaking to the user.
      console.error('Tutor turn failed:', {
        status, code: err?.code, message: err?.message, serverDetail,
      });
      toast.error(msg, { duration: 6000 });
      // Preserve the input so the Retry button can re-send it, and remove
      // the optimistic user bubble (we'll re-add it on successful retry).
      if (trimmed) {
        setTranscript((t) => t.slice(0, -1));
        setFailedInput(trimmed);
      }
    } finally {
      clearTimeout(slowTimer);
      setBusy(false);
    }
  };

  const retryFailedTurn = async () => {
    if (!failedInput) return;
    const input = failedInput;
    setFailedInput(null);
    await sendTutorTurn(input);
  };

  const handleSendText = async (e) => {
    e?.preventDefault();
    if (!textInput.trim() || busy) return;
    const t = textInput.trim();
    setTextInput('');
    await sendTutorTurn(t);
  };

  const startRecording = () => {
    if (!recognitionRef.current) {
      toast.error('Speech recognition not supported — use text input');
      return;
    }
    if (isRecordingRef.current) return; // guard: ignore double-start
    if (audioElementRef.current) audioElementRef.current.pause();
    setInterimText('');
    finalTranscriptRef.current = '';
    isRecordingRef.current = true;
    try {
      recognitionRef.current.start();
      setIsRecording(true);
    } catch (e) {
      // start() throws if the engine is still winding down from a prior
      // session — try once more on the next tick.
      console.debug('SR start deferred:', e?.message);
      setTimeout(() => {
        try {
          recognitionRef.current.start();
          setIsRecording(true);
        } catch (e2) {
          isRecordingRef.current = false;
          console.debug('SR start retry failed:', e2?.message);
        }
      }, 100);
    }
  };

  const stopRecording = useCallback(async () => {
    if (!isRecordingRef.current) return; // already stopped — idempotent
    isRecordingRef.current = false;
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.debug('stopRecording rec.stop:', err);
      }
    }
    setIsRecording(false);

    setTimeout(async () => {
      const finalText = (finalTranscriptRef.current || interimText || '').trim();
      setInterimText('');
      if (!finalText) {
        toast.error('No speech detected');
        return;
      }
      await sendTutorTurn(finalText);
    }, 300);
  }, [interimText]);

  // Keep the ref pointed at the latest stopRecording so window-level
  // pointerup listeners registered inside a mount-only effect can call
  // the current closure.
  useEffect(() => {
    stopRecordingRef.current = stopRecording;
  }, [stopRecording]);

  // Global safety net: while recording, ANY pointerup/pointercancel/blur
  // anywhere on the page — including outside the mic button — releases the
  // mic. Prevents the "cursor drifted off the mic" freeze bug.
  useEffect(() => {
    if (!isRecording) return;
    const release = () => stopRecordingRef.current();
    window.addEventListener('pointerup', release);
    window.addEventListener('pointercancel', release);
    window.addEventListener('blur', release);
    return () => {
      window.removeEventListener('pointerup', release);
      window.removeEventListener('pointercancel', release);
      window.removeEventListener('blur', release);
    };
  }, [isRecording]);

  const handleManualAdvance = async () => {
    if (!window.confirm('Skip this stage and move to the next?')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/learn/topics/${topicId}/advance`, {}, { headers: authHeaders() });
      toast.success('Advanced');
      await fetchTopic();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Advance failed');
    } finally {
      setBusy(false);
    }
  };

  // ==================== Render ====================
  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="overlay-noise" />
        <p className="text-zinc-500 relative z-10">Loading…</p>
      </div>
    );
  }
  if (!topic) return null;

  const status = topic.status || 'draft';
  const stages = topic.stages || [];
  const currentStageIdx = topic.current_stage_index || 0;
  const currentStage = stages[currentStageIdx];

  return (
    <div className="min-h-screen bg-obsidian">
      <div className="overlay-noise" />
      <div className="relative z-10">
        {/* Header */}
        <div className="border-b border-white/5 bg-stone-950/50 backdrop-blur-sm sticky top-0 z-20">
          <div className="max-w-5xl mx-auto px-6 py-5 flex items-center gap-4">
            <Button
              onClick={() => navigate('/learn')}
              className="bg-transparent border border-white/20 text-white hover:bg-white/5 rounded-sm px-3 py-2"
              data-testid="topic-back-button"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex-1 min-w-0">
              <h1
                className="text-xl font-light text-white truncate"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                {topic.title}
              </h1>
              <p className="text-xs text-zinc-400 truncate">{topic.learning_intent}</p>
            </div>
            {stages.length > 0 && (
              <div className="hidden sm:block text-right shrink-0">
                <p className="text-xs font-mono text-zinc-400">
                  Stage {currentStageIdx + 1} / {stages.length}
                </p>
                <p className="text-xs text-zinc-400">{topic.completion_percentage || 0}%</p>
              </div>
            )}
            <ThemeToggle size="sm" className="border border-white/14 rounded-sm shrink-0" />
          </div>
          {stages.length > 0 && (
            <div className="h-1 bg-white/5">
              <div
                className="h-full bg-white/60 transition-all"
                style={{ width: `${topic.completion_percentage || 0}%` }}
                data-testid="progress-bar"
              />
            </div>
          )}
        </div>

        <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">
          {/* ==================== Phase: Materials ==================== */}
          {status === 'draft' && (
            <div data-testid="phase-materials">
              <h2 className="text-lg font-light text-white mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Step 1 — Add reference material
              </h2>
              <p className="text-sm text-zinc-400 mb-6">
                Upload one or more PDFs, DOCX, or TXT files. SAGE will only teach from what you provide.
              </p>

              <label className="block cursor-pointer mb-6">
                <input
                  type="file"
                  accept=".pdf,.docx,.txt,.md"
                  onChange={handleUpload}
                  disabled={busy}
                  className="hidden"
                  data-testid="material-upload-input"
                />
                <div className="threshold-button rounded-sm px-6 py-4 text-base cursor-pointer">
                  <Upload className="w-4 h-4" />
                  <span>{busy ? 'Uploading…' : 'Upload PDF, DOCX or TXT'}</span>
                </div>
              </label>

              <div className="space-y-2" data-testid="materials-list">
                {materials.map((m) => (
                  <div
                    key={m.material_id}
                    className="flex items-center gap-3 bg-stone-950/50 border border-white/5 rounded-sm p-3"
                  >
                    <FileText className="w-4 h-4 text-zinc-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{m.filename}</p>
                      <p className="text-xs text-zinc-400 font-mono">
                        {m.kind.toUpperCase()} · {m.char_count.toLocaleString()} chars
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeleteMaterial(m.material_id)}
                      className="text-zinc-400 hover:text-red-400"
                      aria-label="Remove material"
                      data-testid={`delete-material-${m.material_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {materials.length > 0 && (
                <Button
                  onClick={handleGenerateAssessment}
                  disabled={busy}
                  className="mt-6 threshold-button rounded-sm px-6 py-4 text-base md:text-lg"
                  data-testid="generate-assessment-button"
                >
                  <span>{busy ? 'Generating…' : 'Generate diagnostic assessment'}</span>
                  {!busy && <span className="threshold-button-arrow" aria-hidden="true">→</span>}
                </Button>
              )}
            </div>
          )}

          {/* ==================== Phase: Assessment ==================== */}
          {status === 'assessing' && questions.length > 0 && (
            <div data-testid="phase-assessment">
              <h2 className="text-lg font-light text-white mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Step 2 — Answer the diagnostic
              </h2>
              <p className="text-sm text-zinc-400 mb-6">
                Answer as best you can. Rough guesses are fine — this is how we find your gaps.
              </p>

              <div className="space-y-5">
                {questions.map((q, i) => (
                  <div key={`q-${i}`} className="bg-stone-950/50 border border-white/5 rounded-sm p-5">
                    <p className="text-xs font-mono text-zinc-400 mb-2">Q{i + 1}</p>
                    <p className="text-white mb-3 leading-relaxed">{q}</p>
                    <textarea
                      value={answers[i] || ''}
                      onChange={(e) => {
                        const copy = [...answers];
                        copy[i] = e.target.value;
                        setAnswers(copy);
                      }}
                      placeholder="Your answer…"
                      className="w-full bg-stone-900/60 border border-white/10 text-white p-3 rounded-sm min-h-20 text-sm placeholder:text-zinc-500"
                      data-testid={`answer-input-${i}`}
                    />
                  </div>
                ))}
              </div>

              <Button
                onClick={handleSubmitAnswers}
                disabled={busy}
                className="mt-6 threshold-button rounded-sm px-6 py-4 text-base md:text-lg"
                data-testid="submit-answers-button"
              >
                <span>{busy ? 'Building plan…' : 'Submit & build my plan'}</span>
                {!busy && <span className="threshold-button-arrow" aria-hidden="true">→</span>}
              </Button>
            </div>
          )}

          {/* ==================== Phase: Planning transitional ==================== */}
          {status === 'planning' && (
            <div className="text-center py-16" data-testid="phase-planning">
              <RefreshCw className="w-8 h-8 text-zinc-400 mx-auto mb-4 animate-spin" />
              <p className="text-zinc-300 mb-6">Answers saved. Ready to build your plan.</p>
              <Button
                onClick={handleGeneratePlan}
                disabled={busy}
                className="threshold-button rounded-sm px-6 py-4 text-base md:text-lg"
                data-testid="generate-plan-button"
              >
                <span>{busy ? 'Building plan…' : 'Generate learning plan'}</span>
                {!busy && <span className="threshold-button-arrow" aria-hidden="true">→</span>}
              </Button>
            </div>
          )}

          {/* ==================== Phase: Tutoring / Complete ==================== */}
          {(status === 'tutoring' || status === 'complete') && stages.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6" data-testid="phase-tutoring">
              {/* Left rail: stages */}
              <aside className="space-y-2">
                <p className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-3">
                  Plan
                </p>
                {stages.map((s, i) => {
                  const isActive = i === currentStageIdx && status !== 'complete';
                  const isDone = s.status === 'completed';
                  // Completed OR previously-covered stages can be
                  // revisited by clicking the card. The currently-active
                  // stage already lives in the right pane, so skip it.
                  const canRepeat = isDone && !isActive;
                  return (
                    <button
                      key={`stage-${i}`}
                      type="button"
                      onClick={canRepeat ? () => repeatStage(i) : undefined}
                      disabled={!canRepeat || busy}
                      title={canRepeat ? 'Click to repeat this lesson' : undefined}
                      className={`w-full text-left p-3 rounded-sm border text-sm transition-all ${
                        isActive
                          ? 'bg-white/5 border-white/20 text-white cursor-default'
                          : isDone
                          ? 'bg-stone-950/50 border-white/5 text-zinc-400 hover:bg-stone-950/80 hover:border-white/15 cursor-pointer disabled:opacity-50 disabled:hover:bg-stone-950/50 disabled:hover:border-white/5'
                          : 'bg-stone-950/50 border-white/5 text-zinc-400 cursor-default'
                      }`}
                      data-testid={`stage-item-${i}`}
                    >
                      <div className="flex items-start gap-2">
                        {isDone ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                        ) : isActive ? (
                          <Circle className="w-4 h-4 text-white mt-0.5 shrink-0 fill-white/30" />
                        ) : (
                          <Circle className="w-4 h-4 text-zinc-500 mt-0.5 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium leading-snug">{s.title}</p>
                          <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">
                            {s.concept}
                          </p>
                          {canRepeat && (
                            <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1 flex items-center gap-1">
                              <RefreshCw className="w-2.5 h-2.5" />
                              Repeat lesson
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}

                {/* Notes */}
                {notes.length > 0 && (
                  <div className="mt-6">
                    <p className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-3">
                      Session notes
                    </p>
                    <div className="space-y-2">
                      {notes.map((n) => (
                        <div
                          key={n.note_id}
                          className="p-3 bg-stone-950/50 border border-white/5 rounded-sm"
                          data-testid={`note-${n.note_id}`}
                        >
                          <p className="text-sm text-white truncate">{n.title}</p>
                          <p className="text-xs text-zinc-400 mb-2">{n.stage_name}</p>
                          <LessonSummaryMenu
                            topicId={topicId}
                            stageIdx={n.stage_index}
                            stageTitle={n.stage_name || n.title}
                            authHeaders={authHeaders}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </aside>

              {/* Right: tutor conversation */}
              <section>
                {currentStage && status !== 'complete' && (
                  <div className="bg-stone-950/50 border border-white/10 rounded-sm p-5 mb-4">
                    <p className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-1">
                      Current stage
                    </p>
                    <h3 className="text-lg text-white mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      {currentStage.title}
                    </h3>
                    <p className="text-sm text-zinc-400">{currentStage.objective}</p>
                  </div>
                )}

                {status === 'complete' && (
                  <div className="bg-stone-950/50 border border-green-500/30 rounded-sm p-6 mb-4 text-center">
                    <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-3" />
                    <h3 className="text-xl text-white mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      Plan complete
                    </h3>
                    <p className="text-sm text-zinc-400">
                      Download your notes from the left rail. Well done.
                    </p>
                  </div>
                )}

                {/* Voice-first mode — no visible transcript.
                    We intentionally omit user + SAGE message bubbles and the
                    live interim-recording caption to prevent layout growth
                    from pushing the mic button around while it's held. The
                    replay-audio affordance is surfaced instead in the control
                    row below. */}
                <div className="min-h-[8rem] flex items-center justify-center" data-testid="tutor-status">
                  {status === 'tutoring' && (
                    isSpeaking ? (
                      /* Match the LiveCoaching "SAGE is speaking" aesthetic —
                         Sparkles + animated waveform pips so the moment reads
                         as her presence, not a generic loader. */
                      <div
                        className="flex items-center gap-3 bg-stone-900/80 backdrop-blur-sm border border-white/10 rounded-full px-6 py-3"
                        data-testid="sage-speaking-indicator"
                      >
                        <Sparkles
                          className="w-4 h-4 text-amber-300/90 animate-pulse"
                          aria-hidden="true"
                        />
                        <div className="flex gap-1" aria-hidden="true">
                          <span className="w-1.5 h-4 bg-white/60 rounded-full animate-pulse" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-1.5 h-6 bg-white/80 rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></span>
                          <span className="w-1.5 h-4 bg-white/60 rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></span>
                        </div>
                        <span className="text-sm text-zinc-300">SAGE is speaking…</span>
                      </div>
                    ) : (
                      <p className="text-zinc-400 text-sm text-center">
                        {busy
                          ? 'SAGE is thinking…'
                          : transcript.length === 0
                          ? 'SAGE will begin teaching in a moment…'
                          : 'Hold the mic to answer, or type your response below.'}
                      </p>
                    )
                  )}
                </div>

                {/* Controls */}
                {status === 'tutoring' && (
                  <div className="space-y-3">
                    {/* Retry banner — appears only when the last turn failed */}
                    {failedInput && (
                      <div
                        className="flex items-center justify-between gap-3 bg-red-500/10 border border-red-500/30 rounded-sm px-4 py-3"
                        data-testid="retry-banner"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-mono uppercase tracking-widest text-red-300 mb-1">
                            Your last message wasn’t sent
                          </p>
                          <p className="text-sm text-zinc-200 truncate">“{failedInput}”</p>
                        </div>
                        <Button
                          type="button"
                          onClick={retryFailedTurn}
                          disabled={busy}
                          className="frost-button rounded-sm px-4 py-2 text-sm shrink-0"
                          data-testid="retry-turn-button"
                        >
                          Retry
                        </Button>
                        <button
                          type="button"
                          onClick={() => setFailedInput(null)}
                          className="text-zinc-400 hover:text-white text-xs shrink-0"
                          aria-label="Dismiss"
                          data-testid="dismiss-retry-button"
                        >
                          Dismiss
                        </button>
                      </div>
                    )}
                    {/* Voice + text row */}
                    <form onSubmit={handleSendText} className="flex gap-2">
                      <button
                        type="button"
                        onPointerDown={(e) => {
                          e.preventDefault();
                          // Capture the pointer so pointerup fires on this
                          // element regardless of where the finger/cursor
                          // ends up. Combined with the window-level safety
                          // net in stopRecording, this eliminates the
                          // "mic stuck on" freeze if the user drifts off
                          // the button while holding.
                          try {
                            e.currentTarget.setPointerCapture?.(e.pointerId);
                          } catch (err) {
                            if (process.env.NODE_ENV !== 'production') {
                              console.debug('setPointerCapture failed:', err?.message);
                            }
                          }
                          startRecording();
                        }}
                        onPointerUp={() => stopRecording()}
                        onPointerCancel={() => stopRecording()}
                        onClick={() => {
                          // Escape hatch: if the recording ever gets stuck
                          // (shouldn't happen with pointer capture + window
                          // safety net, but defensive), a plain click on
                          // the mic while recording will release it.
                          if (isRecordingRef.current) stopRecording();
                        }}
                        disabled={busy || isSpeaking}
                        className={`shrink-0 w-12 h-12 rounded-full border transition-all flex items-center justify-center touch-none select-none ${
                          isRecording
                            ? 'bg-red-500/20 border-red-500 text-red-400 scale-110'
                            : 'bg-white/5 border-white/20 text-white hover:bg-white/10'
                        } disabled:opacity-40 disabled:cursor-not-allowed`}
                        aria-label={isRecording ? 'Release to send' : 'Hold to speak'}
                        aria-pressed={isRecording}
                        data-testid="voice-mic-button"
                      >
                        {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                      </button>
                      <Input
                        value={textInput}
                        onChange={(e) => setTextInput(e.target.value)}
                        placeholder={busy ? 'SAGE is thinking…' : 'Type your answer, or hold the mic to speak'}
                        disabled={busy}
                        className="flex-1 bg-stone-900/60 border border-white/10 text-white"
                        data-testid="tutor-text-input"
                      />
                      <Button
                        type="submit"
                        disabled={busy || !textInput.trim()}
                        className="frost-button rounded-sm px-5 py-2"
                        data-testid="tutor-send-button"
                      >
                        Send
                      </Button>
                    </form>

                    {/* Bottom control row — reserved height keeps the layout
                        rock-steady while recording so the mic never moves. */}
                    <div className="flex justify-between items-center h-6 pt-2">
                      <div className="flex items-center gap-3">
                        {lastAudioB64 && (
                          <button
                            type="button"
                            onClick={replayLastAudio}
                            className="text-xs text-zinc-400 hover:text-white flex items-center gap-1"
                            aria-label={isSpeaking ? 'SAGE is speaking' : 'Play SAGE audio again'}
                            data-testid="replay-audio-button"
                          >
                            <Volume2 className={`w-3.5 h-3.5 ${isSpeaking ? 'text-amber-300 animate-pulse' : ''}`} />
                            {isSpeaking ? 'Speaking…' : 'Play again'}
                          </button>
                        )}
                      </div>
                      <button
                        onClick={handleManualAdvance}
                        disabled={busy}
                        className="text-xs text-zinc-400 hover:text-white flex items-center gap-1"
                        data-testid="manual-advance-button"
                      >
                        Skip stage <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                )}
              </section>
            </div>
          )}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
};

export default LearnTopic;
